import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { SalesFunnelDataService, Package } from '../shared/sales-funnel-data.service';
import { ISalesFunnelLayoutChild } from '../shared/sales-funnel-child.module';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

type LagoPrivilege = {
  code: string;
  name?: string;
  value_type?: string;
  value?: number;
};

type LagoEntitlement = {
  code: string;
  name?: string;
  description?: string;
  privileges?: LagoPrivilege[];
};

type LagoPlanDto = {
  lago_id: string;
  name: string;
  invoice_display_name?: string;
  created_at?: string;
  code: string; // res_150mbps, bus_500mbps etc.
  interval: string; // monthly
  description?: string;
  amount_cents: number;
  amount_currency: string;
  trial_period?: number;
  pay_in_advance?: boolean;
  entitlements?: LagoEntitlement[];
};

// Extend existing Package with extra UI fields
export type PackageView = Package & {
  description?: string;
  amountCents?: number;
  currency?: string;
  interval?: string;
  lagoCode?: string;
};

@Component({
  selector: 'app-package-offer',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './package-offer-component.html',
  styleUrls: ['./package-offer-component.scss'],
})
export class PackageOfferComponent implements OnInit, ISalesFunnelLayoutChild {
  private router = inject(Router);
  private http = inject(HttpClient);
  private dataService = inject(SalesFunnelDataService);

  private readonly baseUrl: string = environment.dataApiBase;

  packages = signal<PackageView[]>([]);
  selectedPackage: PackageView | null = null;

  discount: string | number = '';
  salesFunnelIndex = 1;

  ngOnInit(): void {
    const data = this.dataService.getData();
    const url = `${this.baseUrl}/price-plans`;

    if (data.selectedPackage) {
      this.selectedPackage = data.selectedPackage as PackageView;
    }
    if (data.discount) {
      this.discount = data.discount;
    }

    const isBusiness = data.lead?.id?.at(-1) || '0';

    console.info(`[PackageOffer] Fetching packages where business=${isBusiness}`);

    this.http.get<LagoPlanDto[]>(`${url}/business/${isBusiness}`).subscribe({
      next: (plans) => {
        const mapped = plans.map((p) => this.mapLagoPlanToPackageView(p));
        // Optional: sort by speed descending
        mapped.sort((a, b) => (b.speed ?? 0) - (a.speed ?? 0));
        this.packages.set(mapped);
      },
      error: (err) => {
        console.error('[PackageOffer] Failed to load price plans', err);
        this.packages.set([]);
      },
    });
  }

  selectPackage(pkg: PackageView): void {
    this.selectedPackage = pkg;
    this.dataService.updatePackage(pkg as Package);
  }

  goBack(): void {
    this.router.navigate(['/sales-funnel']);
  }

  goToCustomerDetails(): void {
    if (this.selectedPackage) {
      if (this.discount) {
        this.dataService.updateDiscount(Number(this.discount) || 0);
      }
      this.router.navigate(['/customer-details']);
    }
  }

  // ----------------------------
  // Mapping helpers
  // ----------------------------

  private mapLagoPlanToPackageView(plan: LagoPlanDto): PackageView {
    const speedMbps = this.extractMaxBandwidthMbps(plan);
    const business = plan.code?.startsWith('bus_') ?? false;
    const tier = this.deriveTierFromSpeed(speedMbps, plan.code);
    const formattedPrice = this.formatMoney(plan.amount_cents, plan.amount_currency);

    return {
      // REQUIRED by your Package interface ✅
      // Use speed as the numeric "data" field (or swap to plan.amount_cents if preferred)
      data: speedMbps ?? 0,

      // existing fields your UI expects
      name: plan.name,
      tier: tier as any,
      business: business as any,
      speed: speedMbps ?? -1,
      price: formattedPrice as any,

      // if your Package interface has these optional/required fields, keep them
      badge: undefined as any,
      features: undefined as any,

      // extra display fields
      description: plan.description ?? '',
      amountCents: plan.amount_cents,
      currency: plan.amount_currency,
      interval: plan.interval,
      lagoCode: plan.code,
    };
  }

  private extractMaxBandwidthMbps(plan: LagoPlanDto): number | null {
    const entitlements = plan.entitlements ?? [];
    const maxBandwidth = entitlements.find((e) => e.code === 'max_bandwidth');
    const privileges = maxBandwidth?.privileges ?? [];
    const mbps = privileges.find((p) => p.code === 'mbps')?.value;
    return typeof mbps === 'number' ? mbps : null;
  }

  private deriveTierFromSpeed(speedMbps: number | null, code: string): string {
    if (speedMbps == null) {
      return code?.includes('1gbps') ? 'platinum' : 'basic';
    }
    if (speedMbps <= 150) return 'basic';
    if (speedMbps <= 300) return 'standard';
    if (speedMbps <= 500) return 'premium';
    if (speedMbps <= 1000) return 'platinum';
    return 'bonus';
  }

  private formatMoney(amountCents: number, currency: string): string {
    const amount = (amountCents ?? 0) / 100;
    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currency || 'GBP',
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return `£${amount.toFixed(2)}`;
    }
  }
}
